import { useState, type FormEvent } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { TextField } from '@/components/TextField/TextField';

import styles from './CreateAccountForm.module.css';

export interface AccountDetails {
  firstName: string;
  lastName: string;
  email: string;
  businessName: string;
}

export interface CreateAccountFormProps {
  initial?: Partial<AccountDetails>;
  /** Fix the email, for an identity that has already proved it. */
  emailLocked?: boolean;
  pending?: boolean;
  error?: string | null;
  notice?: string | null;
  submitLabel?: string;
  onSubmit: (details: AccountDetails) => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}

type Errors = Partial<Record<keyof AccountDetails, string>>;

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(details: AccountDetails): Errors {
  const errors: Errors = {};
  if (!details.firstName) {
    errors.firstName = 'Enter your first name.';
  }
  if (!details.lastName) {
    errors.lastName = 'Enter your last name.';
  }
  if (!EMAIL_SHAPE.test(details.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!details.businessName) {
    errors.businessName = 'Enter your business name.';
  }
  return errors;
}

/** The new-account details. Knows nothing of Turnstile or Arcade. */
export function CreateAccountForm({
  initial = {},
  emailLocked = false,
  pending = false,
  error = null,
  notice = null,
  submitLabel = 'Continue',
  onSubmit,
  secondaryLabel,
  onSecondary,
}: CreateAccountFormProps) {
  const [details, setDetails] = useState<AccountDetails>({
    firstName: initial.firstName ?? '',
    lastName: initial.lastName ?? '',
    email: initial.email ?? '',
    businessName: initial.businessName ?? '',
  });
  const [errors, setErrors] = useState<Errors>({});

  function field(key: keyof AccountDetails) {
    return {
      value: details[key],
      error: errors[key],
      onChange: (event: { target: { value: string } }) =>
        setDetails({ ...details, [key]: event.target.value }),
    };
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed: AccountDetails = {
      firstName: details.firstName.trim(),
      lastName: details.lastName.trim(),
      email: details.email.trim(),
      businessName: details.businessName.trim(),
    };
    const found = validate(trimmed);
    setErrors(found);
    if (Object.keys(found).length === 0) {
      onSubmit(trimmed);
    }
  }

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Developer portal</span>
        <h1 className={styles.heading}>Create your account</h1>
        <p className={styles.lede}>
          {emailLocked
            ? 'One more step: tell us who you are.'
            : 'Tell us who you are. We will email you a one-time pin to confirm your address.'}
        </p>
      </div>

      <div className={styles.fields}>
        {notice ? <Callout>{notice}</Callout> : null}
        {error ? <Callout tone="danger">{error}</Callout> : null}

        <div className={styles.row}>
          <TextField
            label="First name"
            name="given-name"
            autoComplete="given-name"
            autoFocus
            required
            {...field('firstName')}
          />
          <TextField
            label="Last name"
            name="family-name"
            autoComplete="family-name"
            required
            {...field('lastName')}
          />
        </div>
        <TextField
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@company.com"
          readOnly={emailLocked}
          required
          {...field('email')}
        />
        <TextField
          label="Business name"
          name="organization"
          autoComplete="organization"
          required
          {...field('businessName')}
        />
      </div>

      <div className={styles.actions}>
        <Button type="submit" size="lg" block loading={pending}>
          {submitLabel}
        </Button>
        {secondaryLabel && onSecondary ? (
          <Button
            type="button"
            variant="ghost"
            size="lg"
            block
            onClick={onSecondary}
            disabled={pending}
          >
            {secondaryLabel}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
